/**
 * UndoSnackbar (A43.2) — undo-снекбар для «лёгких» удалений вместо модалки:
 * «{текст} · ОТМЕНИТЬ», автоскрытие 5с с тонкой полосой-таймером. Портал в
 * body над safe-bottom, z ниже шитов (SheetDialog — var(--z-modal)).
 *
 * Контракт deferred-DELETE: ровно один из onUndo/onExpire вызывается ровно
 * один раз. Повторный show вытесняет прежний — прежний немедленно onExpire
 * (его сеть улетает). Unmount хоста = немедленный onExpire (отложенный
 * DELETE обязан улететь при уходе с экрана). Выгрузка/сворачивание страницы
 * (pagehide, visibilitychange→hidden — TMA-WebView сворачивают постоянно) =
 * тоже немедленный onExpire, иначе deferred-DELETE терялся бы при F5/киле
 * WebView; сама мутация обязана уходить fetch'ем с keepalive.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import css from "./UndoSnackbar.module.css";

export interface UndoSnackbarItem {
  /** Текст слева, например «Чайная «X» удалена». */
  label: string;
  /** «ОТМЕНИТЬ»: вернуть строку без сети. */
  onUndo: () => void;
  /** 5с истекли / вытеснен / хост ушёл: deferred-мутация улетает. */
  onExpire: () => void;
}

export interface UndoSnackbarApi {
  show: (item: UndoSnackbarItem) => void;
  /** Рендерить в дереве хоста — unmount хоста гасит снекбар с onExpire. */
  element: ReactNode;
}

/** Автоскрытие; полоса-таймер в CSS (snackBar 5s) обязана совпадать. */
const TTL_MS = 5000;

export function useUndoSnackbar(): UndoSnackbarApi {
  /* n — React key: повторный show ремаунтит снекбар, полоса-таймер и
     анимация появления перезапускаются (паттерн flashOk useBranchConfig). */
  const [cur, setCur] = useState<{ item: UndoSnackbarItem; n: number } | null>(null);
  const liveRef = useRef<UndoSnackbarItem | null>(null);
  const timerRef = useRef(0);

  const settle = useCallback((how: "undo" | "expire") => {
    const item = liveRef.current;
    if (!item) return;
    liveRef.current = null;
    window.clearTimeout(timerRef.current);
    setCur(null);
    if (how === "undo") item.onUndo();
    else item.onExpire();
  }, []);

  const show = useCallback((item: UndoSnackbarItem) => {
    /* Вытеснение: прежний немедленно onExpire — его DELETE улетает сейчас. */
    const prev = liveRef.current;
    if (prev) {
      liveRef.current = null;
      window.clearTimeout(timerRef.current);
      prev.onExpire();
    }
    liveRef.current = item;
    setCur((c) => ({ item, n: (c?.n ?? 0) + 1 }));
    timerRef.current = window.setTimeout(() => settle("expire"), TTL_MS);
  }, [settle]);

  /* Unmount хоста = немедленный onExpire (deferred-DELETE обязан улететь). */
  useEffect(() => () => {
    const item = liveRef.current;
    liveRef.current = null;
    window.clearTimeout(timerRef.current);
    if (item) item.onExpire();
  }, []);

  /* Закрытие/сворачивание страницы: unmount-cleanup не срабатывает на F5,
     закрытии вкладки и киле Telegram-WebView — deferred-DELETE улетает
     немедленно (его fetch обязан жить с keepalive, чтобы пережить выгрузку). */
  useEffect(() => {
    const bye = () => settle("expire");
    const vis = () => { if (document.visibilityState === "hidden") settle("expire"); };
    window.addEventListener("pagehide", bye);
    document.addEventListener("visibilitychange", vis);
    return () => {
      window.removeEventListener("pagehide", bye);
      document.removeEventListener("visibilitychange", vis);
    };
  }, [settle]);

  const element = cur
    ? createPortal(
        <div key={cur.n} className={css.snack} role="status">
          <span className={css.lbl}>{cur.item.label}</span>
          <span className={css.dot} aria-hidden="true">·</span>
          <button type="button" className={css.undo} onClick={() => settle("undo")}>
            Отменить
          </button>
          <span className={css.bar} aria-hidden="true" />
        </div>,
        document.body,
      )
    : null;

  return { show, element };
}
